import { Component } from '@theme/component';

/**
 * Handles copying text to clipboard, from an event like a click.
 * Optionally, reveals a success message after copying.
 * @extends {Component}
 */
class CopyToClipboardComponent extends Component {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hideTimer;

  copyToClipboard() {
    const copyContent = this.getAttribute('text-to-copy');

    if (!copyContent) return;

    const onCopied = () => this.#showSuccess();

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(copyContent).then(onCopied).catch(() => this.#fallbackCopy(copyContent, onCopied));
    } else {
      this.#fallbackCopy(copyContent, onCopied);
    }
  }

  /**
   * Reveals the success message. If the `auto-hide` attribute is set (milliseconds),
   * the message hides itself again after that delay, restarting the timer on repeat clicks.
   * Without the attribute, the message stays visible once shown (original behavior).
   */
  #showSuccess() {
    const copySuccessMessage = this.refs.copySuccessMessage;

    if (!(copySuccessMessage instanceof Element)) return;

    copySuccessMessage.classList.remove('visually-hidden');

    const hideAfter = Number(this.getAttribute('auto-hide'));

    if (!hideAfter) return;

    clearTimeout(this.#hideTimer);
    this.#hideTimer = setTimeout(() => {
      copySuccessMessage.classList.add('visually-hidden');
    }, hideAfter);
  }

  /**
   * Fallback for browsers/contexts (e.g. insecure origins, restrictive iframes
   * like the theme editor preview) where the async Clipboard API is unavailable.
   *
   * @param {string} text
   * @param {() => void} onCopied
   */
  #fallbackCopy(text, onCopied) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;pointer-events:none';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      if (document.execCommand('copy')) onCopied();
    } catch {
      // Copying isn't supported in this context; fail silently.
    }

    document.body.removeChild(textArea);
  }
}

if (!customElements.get('copy-to-clipboard-component')) {
  customElements.define('copy-to-clipboard-component', CopyToClipboardComponent);
}
